import { useState } from "react";
import { X, Upload, CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";
import api from "../api/client";
import { toast } from "sonner";

const PLACEHOLDER = "Jane Smith, jane@example.com\nRaj Patel, raj@example.com\n…or upload a CSV below";

export default function BulkImportDialog({ open, onClose, onDone }) {
  const [text, setText] = useState("");
  const [sendWelcome, setSendWelcome] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  if (!open) return null;

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const content = await f.text();
    setText((prev) => (prev.trim() ? `${prev.trim()}\n${content}` : content));
    e.target.value = "";
  };

  const lineCount = text.split("\n").filter((l) => l.trim()).length;

  const submit = async () => {
    if (!text.trim()) return toast.error("Paste at least one line or upload a CSV");
    setBusy(true);
    try {
      const { data } = await api.post("/workers/bulk", { text, send_welcome: sendWelcome });
      setResult(data);
      if (data.created.length) {
        toast.success(`${data.created.length} team member${data.created.length === 1 ? "" : "s"} added`);
        onDone();
      }
      if (!data.created.length && data.skipped.length) toast.error("Nothing imported — see the details below");
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(Array.isArray(d) ? d.map((x) => x.msg).join("; ") : d || "Import failed");
    } finally {
      setBusy(false);
    }
  };

  const close = () => { setText(""); setResult(null); setSendWelcome(true); onClose(); };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4 overflow-y-auto" onClick={close}>
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white border border-gray-300 max-w-xl w-full p-6 rounded-sm shadow-2xl my-6"
        data-testid="bulk-import-dialog"
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <h4 className="font-black text-xl tracking-tight">Bulk import team members</h4>
            <p className="text-xs text-gray-500 mt-1">One per line: <span className="font-mono">Name, email</span>. Headers are ignored.</p>
          </div>
          <button type="button" onClick={close} data-testid="close-bulk-import"><X className="w-5 h-5" /></button>
        </div>

        {!result && (
          <>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={PLACEHOLDER}
              rows={8}
              spellCheck={false}
              data-testid="bulk-import-textarea"
              className="w-full border border-gray-300 px-3 py-2 rounded-sm text-sm font-mono"
            />
            <div className="flex flex-wrap items-center justify-between gap-3 mt-2">
              <label className="inline-flex items-center gap-1.5 border border-gray-300 px-3 py-1.5 text-xs font-semibold rounded-sm hover:bg-gray-100 cursor-pointer">
                <Upload className="w-3.5 h-3.5" /> Upload CSV
                <input type="file" accept=".csv,.txt,text/csv,text/plain" className="hidden" onChange={onFile} data-testid="bulk-import-file" />
              </label>
              <span className="text-xs text-gray-500" data-testid="bulk-import-count">{lineCount} line{lineCount === 1 ? "" : "s"}</span>
            </div>

            <label className="flex items-start gap-2 mt-4 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={sendWelcome}
                onChange={(e) => setSendWelcome(e.target.checked)}
                data-testid="bulk-send-welcome-checkbox"
                className="mt-0.5"
              />
              <span>
                Email each person their login details and starter password
                <span className="block text-xs text-gray-500">They'll be asked to set their own password on first sign-in.</span>
              </span>
            </label>

            <button
              type="button"
              onClick={submit}
              disabled={busy || !lineCount}
              data-testid="submit-bulk-import"
              className="w-full bg-[#002FA7] text-white py-2.5 mt-4 font-bold rounded-sm hover:bg-[#002277] disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {busy && <Loader2 className="w-4 h-4 animate-spin" />}
              {busy ? "IMPORTING…" : `IMPORT ${lineCount || ""} TEAM MEMBER${lineCount === 1 ? "" : "S"}`}
            </button>
          </>
        )}

        {result && <ImportResult result={result} onClose={close} />}
      </div>
    </div>
  );
}

function ImportResult({ result, onClose }) {
  return (
    <div data-testid="bulk-import-result">
      <div className="flex items-center gap-2 text-green-800 bg-green-50 border border-green-200 px-3 py-2 rounded-sm text-sm mb-3">
        <CheckCircle2 className="w-4 h-4" />
        <span data-testid="bulk-import-created-count">{result.created.length} added</span>
        <span className="text-xs text-green-700 ml-auto">Starter password: <span className="font-mono font-bold">{result.temp_password}</span></span>
      </div>
      {result.created.length > 0 && (
        <ul className="text-sm mb-3 max-h-40 overflow-y-auto border border-gray-200 rounded-sm divide-y">
          {result.created.map((c) => (
            <li key={c.id} className="flex items-center justify-between px-3 py-1.5" data-testid={`bulk-created-${c.email}`}>
              <span>{c.name} <span className="text-gray-500 font-mono text-xs">{c.email}</span></span>
              <span className={`text-[10px] uppercase tracking-wider font-bold ${c.welcome_email_sent ? "text-green-700" : "text-gray-400"}`}>
                {c.welcome_email_sent ? "emailed" : "not emailed"}
              </span>
            </li>
          ))}
        </ul>
      )}
      {result.skipped.length > 0 && (
        <>
          <div className="flex items-center gap-2 text-amber-800 bg-amber-50 border border-amber-200 px-3 py-2 rounded-sm text-sm mb-2">
            <AlertTriangle className="w-4 h-4" />
            <span data-testid="bulk-import-skipped-count">{result.skipped.length} skipped</span>
          </div>
          <ul className="text-sm mb-3 max-h-40 overflow-y-auto border border-gray-200 rounded-sm divide-y">
            {result.skipped.map((s, i) => (
              <li key={`${s.email}-${i}`} className="flex items-center justify-between px-3 py-1.5 gap-2">
                <span className="truncate">{s.name || "—"} <span className="text-gray-500 font-mono text-xs">{s.email || ""}</span></span>
                <span className="text-xs text-amber-700 shrink-0">{s.reason}</span>
              </li>
            ))}
          </ul>
        </>
      )}
      <button type="button" onClick={onClose} data-testid="bulk-import-done" className="w-full border border-gray-800 py-2.5 mt-1 font-bold rounded-sm hover:bg-gray-100">
        DONE
      </button>
    </div>
  );
}
